import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { getAllWomenProducts } from '../data/womensProducts';
import { getAllMenProducts } from '../data/mensProducts';
import { getAllKidsProducts } from '../data/kidsProducts';
import WomenProductCard from '../components/WomenProductCard';
import MenProductCard from '../components/MenProductCard';
import KidsProductCard from '../components/KidsProductCard';

type Section = 'all' | 'women' | 'men' | 'kids';

const SearchPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const [query, setQuery] = useState(searchParams.get('q') || '');
  const [section, setSection] = useState<Section>('all');

  useEffect(() => {
    setQuery(searchParams.get('q') || '');
  }, [searchParams]);

  const q = query.toLowerCase().trim();

  const womenResults = q ? getAllWomenProducts().filter(p =>
    p.name.toLowerCase().includes(q) || p.category.toLowerCase().includes(q) || p.description.toLowerCase().includes(q)
  ) : [];

  const menResults = q ? getAllMenProducts().filter(p =>
    p.name.toLowerCase().includes(q) || p.category.toLowerCase().includes(q) || p.description.toLowerCase().includes(q)
  ) : [];

  const kidsResults = q ? getAllKidsProducts().filter(p =>
    p.name.toLowerCase().includes(q) || p.category.toLowerCase().includes(q) || p.description.toLowerCase().includes(q)
  ) : [];

  const totalResults = womenResults.length + menResults.length + kidsResults.length;

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (query.trim()) setSearchParams({ q: query.trim() });
  };

  const sections: { key: Section; label: string; count: number }[] = [
    { key: 'all', label: 'All', count: totalResults },
    { key: 'women', label: "Women's", count: womenResults.length },
    { key: 'men', label: "Men's", count: menResults.length },
    { key: 'kids', label: 'Kids', count: kidsResults.length },
  ];

  return (
    <div className="bg-[#FCFCFC]">
      <div className="py-8 px-4 md:px-20">
        <div className="max-w-7xl mx-auto">

          {/* Search bar */}
          <form onSubmit={handleSearch} className="mb-8">
            <div className="relative max-w-2xl">
              <input
                type="text"
                value={query}
                onChange={e => setQuery(e.target.value)}
                placeholder="Search for products..."
                className="w-full pl-5 pr-14 py-4 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-[#DAA520] text-base"
                autoFocus
              />
              <button type="submit" className="absolute right-3 top-1/2 -translate-y-1/2 bg-[#DAA520] text-white p-2 rounded-lg hover:bg-[#c5941c]">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </button>
            </div>
          </form>

          {!q ? (
            <div className="text-center py-20">
              <div className="text-6xl mb-4">🔍</div>
              <p className="text-gray-500 text-lg">Search for dresses, kurtas, jackets and more...</p>
            </div>
          ) : totalResults === 0 ? (
            <div className="text-center py-20">
              <div className="text-6xl mb-4">😕</div>
              <h2 className="text-xl font-semibold mb-2">No results for "{q}"</h2>
              <p className="text-gray-500">Try different keywords or browse our collections</p>
              <div className="flex gap-3 justify-center mt-6">
                <button onClick={() => navigate('/women')} className="bg-[#E91E63] text-white px-5 py-2 rounded-lg text-sm">Women's</button>
                <button onClick={() => navigate('/men')} className="bg-[#2196F3] text-white px-5 py-2 rounded-lg text-sm">Men's</button>
                <button onClick={() => navigate('/kids')} className="bg-[#4CAF50] text-white px-5 py-2 rounded-lg text-sm">Kids</button>
              </div>
            </div>
          ) : (
            <>
              <div className="flex items-center justify-between mb-4">
                <p className="text-gray-600">{totalResults} results for "<span className="font-semibold text-gray-800">{q}</span>"</p>
              </div>

              {/* Section tabs */}
              <div className="flex gap-2 mb-6 flex-wrap">
                {sections.filter(s => s.key === 'all' || s.count > 0).map(s => (
                  <button
                    key={s.key}
                    onClick={() => setSection(s.key)}
                    className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors ${
                      section === s.key ? 'bg-[#DAA520] text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    {s.label} ({s.count})
                  </button>
                ))}
              </div>

              {/* Women's results */}
              {(section === 'all' || section === 'women') && womenResults.length > 0 && (
                <div className="mb-10">
                  {section === 'all' && <h2 className="text-lg font-semibold text-gray-700 mb-4">Women's ({womenResults.length})</h2>}
                  <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
                    {womenResults.map(p => <WomenProductCard key={p.id} product={p} />)}
                  </div>
                </div>
              )}

              {/* Men's results */}
              {(section === 'all' || section === 'men') && menResults.length > 0 && (
                <div className="mb-10">
                  {section === 'all' && <h2 className="text-lg font-semibold text-gray-700 mb-4">Men's ({menResults.length})</h2>}
                  <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
                    {menResults.map(p => <MenProductCard key={p.id} product={p} />)}
                  </div>
                </div>
              )}

              {/* Kids results */}
              {(section === 'all' || section === 'kids') && kidsResults.length > 0 && (
                <div className="mb-10">
                  {section === 'all' && <h2 className="text-lg font-semibold text-gray-700 mb-4">Kids ({kidsResults.length})</h2>}
                  <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
                    {kidsResults.map(p => <KidsProductCard key={p.id} product={p} />)}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default SearchPage;
